import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { monthsAPI } from '../services/api';
import { Todo, Month, MonthUpdate } from '../types';

export const useMonths = (selectedDate: Date) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Get current month data
  const { data: currentMonthData, isLoading: isCurrentMonthLoading } = useQuery({
    queryKey: ['months', 'current'],
    queryFn: async (): Promise<Month> => {
      return await monthsAPI.getCurrent();
    },
    enabled: selectedDate.getMonth() === new Date().getMonth() && 
             selectedDate.getFullYear() === new Date().getFullYear(),
  });

  // Get specific month data for historical dates
  const { data: monthData, isLoading: isMonthLoading } = useQuery({
    queryKey: ['months', format(selectedDate, 'yyyy-MM')],
    queryFn: async (): Promise<Month | null> => {
      try {
        // Get all months and find the matching one
        const months = await monthsAPI.getAll(undefined, 50, 0);
        
        const monthMatch = months.find((m: Month) => 
          m.year === selectedDate.getFullYear() && 
          m.month_number === selectedDate.getMonth() + 1
        );

        return monthMatch || null;
      } catch (err) {
        console.error('Error fetching month data:', err);
        return null;
      }
    },
    enabled: !(selectedDate.getMonth() === new Date().getMonth() && 
              selectedDate.getFullYear() === new Date().getFullYear()),
  });

  // Update month mutation
  const updateMonthMutation = useMutation({
    mutationFn: async ({ monthId, data }: { monthId: string; data: MonthUpdate }) => {
      return await monthsAPI.update(monthId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['months'] });
    },
    onError: (err: unknown) => {
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to update month';
      setError(errorMessage);
    },
  });

  // Update month function
  const updateMonth = async (monthId: string, data: MonthUpdate) => {
    return updateMonthMutation.mutateAsync({ monthId, data });
  };

  // Determine which data to use based on selected date
  const isCurrentMonth = selectedDate.getMonth() === new Date().getMonth() && 
                        selectedDate.getFullYear() === new Date().getFullYear();
  const currentData = isCurrentMonth ? currentMonthData : monthData;
  const isLoading = isCurrentMonth ? isCurrentMonthLoading : isMonthLoading;

  // For now, return empty completed tasks as the API structure doesn't include them
  // This will need to be implemented properly when the months API supports task completion tracking
  return {
    currentMonth: currentData || null,
    completedTasks: [] as Todo[],
    updateMonth,
    isLoading,
    error,
    isUpdating: updateMonthMutation.isPending,
  };
};